// package imports
import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

// CSS imports
import './AdminUsers.css';

// Component imports
import Spinner from '../../components/spinner/Spinner';
import AdminUserCard from '../../components/adminUserCard/AdminUserCard';
import Alert from '../../components/alert/Alert';

// Helper imports
import SectionWrapper from '../../helpers/components/sectionWrapper/SectionWrapper';

// Service imports
import { ApiService } from '../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';

// Type imports
import { SectionContentSize } from '../../types/sectionWrapper/SectionContentSize';
import { AlertSize } from '../../types/alert/AlertSize';
import { User } from '../../types/user/User';
import { AdminUsersResult } from '../../types/user/AdminUsersResult';

type Props = {
  loggedInUser: User;
};

const AdminUsers: React.FC<Props> = ({ loggedInUser }: Props) => {
  const [selectedPage, setSelectedPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [users, setUsers] = useState<User[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [showUnknownError, setShowUnknownError] = useState(false);

  const getUserRequests = useCallback(
    async (tempUsers: User[], page: number) => {
      setIsLoading(true);
      const res: AdminUsersResult | null = await ApiService.getAdminUsers(
        page,
        9
      );
      setIsLoading(false);
      if (res) {
        for (const user of res.users) {
          tempUsers.push(user);
        }
        setUsers(tempUsers);
        setSelectedPage(res.page);
        setPagesCount(res.pages);
      }
    },
    []
  );

  useEffect(() => {
    getUserRequests([], 1);
  }, [getUserRequests]);

  const updateUser = async (id: string, index: number, role: string) => {
    const res: boolean = await ApiService.updateAdminUser(id, role);
    if (res) {
      const tempUsers = users.slice(0, users.length);
      tempUsers[index].role = role;
      setUsers(tempUsers);
    } else {
      setShowUnknownError(true);
    }
  };

  const deleteUser = async (id: string, index: number) => {
    const res: boolean = await ApiService.deleteAdminUser(id);
    if (res) {
      const tempUsers = users.slice(0, users.length);
      tempUsers.splice(index, 1);
      setUsers(tempUsers);
    } else {
      setShowUnknownError(true);
    }
  };

  return (
    <div className='AdminUsers'>
      <SectionWrapper contentWidth={SectionContentSize.MEDIUM}>
        <div className='content'>
          <h2>Admin Users</h2>
          <InfiniteScroll
            dataLength={users.length}
            hasMore={selectedPage !== pagesCount}
            next={() => getUserRequests(users, selectedPage + 1)}
            loader={<Spinner isLoading={true} />}
            className='infinite-scroll-container'
          >
            {users.map((user: User, index: number) => (
              <AdminUserCard
                className='item-card'
                loggedInUser={loggedInUser}
                user={user}
                index={index}
                updateUser={updateUser}
                deleteUser={deleteUser}
                key={index}
              />
            ))}
          </InfiniteScroll>
          {!isLoading && users.length === 0 ? (
            <span className='no-items-found'>No admin users</span>
          ) : null}
        </div>
      </SectionWrapper>
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
    </div>
  );
};

export default AdminUsers;
